/* ═══════════════════════════════════════════════════════════════════════════
   OCCUPATIONAL HEALTH — who must have it, who may see what, and who pays

   A marketplace module: the business subscribes to a provider, its people
   book their own appointments over the video and payment rails that already
   exist, and the platform takes a share. Three rules hold the whole thing
   up, and two of them are the kind that are easy to break by accident.

   ── 1. The duty is not universal, and saying it is would be a lie ─────────

   The duty to be attached to an APPROVED bedriftshelsetjeneste falls on the
   trades named in the regulation, and on everybody else only where their own
   risk assessment says so. So `duty()` answers one of two things and never
   "you must": either the trade is normally named, or it is not and the risk
   decides. Both answers carry the qualifier that the regulation lists
   næringskoder rather than trade names, so the business's own code is the
   fact that settles it.

   A platform that told a law firm it was breaking the law in order to sell
   it a subscription would be doing something worse than bad marketing. And
   telling a building contractor it has no duty is worse still, because they
   might believe it.

   ── 2. The employer never learns why ──────────────────────────────────────

   This is the one that gets designed away, because a dashboard showing five
   named appointments looks better than one showing a number. The service is
   confidential: what an employee consults it about is between them and the
   health personnel. So `employerView()` reduces a set of bookings to counts
   and nothing else — no names, no services, no dates precise enough to work
   backwards from — and it is the ONLY function anything on the business
   surface is allowed to render from.

   The reduction is here rather than in a component on purpose. A component
   that received the full rows and promised to show three fields is one
   refactor away from showing four.

   ── 3. Two flows of money, and neither is hidden ──────────────────────────

   The business pays the provider, through a subscription on this platform.
   The provider pays the platform to be listed. Both are stated on the
   screens — the same arrangement as the payment rails, where a tenant who
   could not tell whose Stripe was whose was the failure that arrangement
   was designed around.

   ── What this module refuses to invent ────────────────────────────────────

   Prices. Nobody has been asked yet, `price` is null on every provider, and
   the arithmetic below is written so a missing figure produces "not agreed"
   rather than nought. A demo that showed 25 kr per employee because it read
   well would be quoting a supplier who has never spoken to us.
   ═══════════════════════════════════════════════════════════════════════ */
import { rules as rulesFor, HOME } from '../law/index.js';
import { bhtDuty } from '../industries/index.js';
import { provider, employeeService } from '../data/health.js';

/**
 * Whether this business has the duty, as far as anything here can say.
 *
 * Never "yes, you must" and never "no, you need not". The first is not this
 * platform's to say about a particular business and the second is not true
 * of anybody.
 */
export function duty(industryCode, country = HOME) {
  const h = rulesFor(country).health;
  const listed = bhtDuty(industryCode) === 'listed';
  return {
    listed,
    /* The word a screen leads with. "Named" and "not named" are facts about
       a list; "required" and "not required" are claims about a business. */
    label: listed ? 'Normally named in the regulation' : 'Not named in the regulation',
    what: listed ? h.listedWhat : h.riskWhat,
    caveat: h.byCode,
    cite: listed ? h.cite : h.riskCite,
    approvedWhat: h.approvedWhat,
    payer: h.payer,
  };
}

/* The states a subscription passes through, and what each one means for the
   people who would be booking against it. */
export const STATES = {
  none: {
    label: 'No provider',
    what: 'Nobody here can book an appointment, because there is nothing to '
      + 'book against.',
    live: false,
  },
  asked: {
    label: 'Waiting for the provider',
    what: 'The provider has been asked and has not answered. Nothing is '
      + 'charged and nobody can book yet.',
    live: false,
  },
  live: {
    label: 'Active',
    what: 'Everybody on the roster can book, from their own screen.',
    live: true,
  },
  ended: {
    label: 'Ended',
    what: 'Booking is closed. Appointments already made are the provider\'s '
      + 'to honour or cancel, not this platform\'s.',
    live: false,
  },
};

export const usable = sub => !!STATES[sub?.state]?.live;

/**
 * What the business is billed, and by whom.
 *
 * Two lines because there are two of them, and they go to different people.
 * A null price is NOT zero: it means nobody has agreed one, and the caller
 * has to print that rather than a number. `total` is null unless every line
 * has a figure — a partial total is a quote with a hole in it.
 */
export function bill(providerId, heads = 0, commission = null) {
  const p = provider(providerId);
  const n = Math.max(0, Number(heads) || 0);
  const each = p?.price?.perPerson ?? null;
  const base = p?.price?.perMonth ?? null;

  const lines = [
    { what: `${p?.name || 'The provider'} — monthly`, kr: base, to: 'the provider' },
    { what: `${p?.name || 'The provider'} — per person`, kr: each == null ? null : each * n, to: 'the provider' },
  ];
  const known = lines.every(l => l.kr != null);
  return {
    heads: n,
    lines,
    total: known ? lines.reduce((s, l) => s + l.kr, 0) : null,
    /* What the platform would take. Also null, and deliberately visible to
       the business rather than buried: a marketplace that hid its cut from
       the buyer would be inviting the question it least wants asked. */
    commission: commission ?? null,
    say: known
      ? null
      : 'No price has been agreed with this provider yet, so this screen does '
        + 'not show one. It will not invent a figure — a number in a demo has '
        + 'a way of becoming a number on a quote.',
  };
}

/**
 * What the employer is shown about the use of the service.
 *
 * Counts. Not names, not services, not dates. The only function on the
 * business side allowed to touch a booking, so that a screen cannot show a
 * field it was never given.
 *
 * `months` is a count of distinct months with activity rather than a list of
 * days, because a business with two employees and one appointment in March
 * can work backwards from a date and a rota. Small numbers are identifying,
 * and this is the case where that matters.
 */
export function employerView(bookings = [], country = HOME) {
  const live = bookings.filter(b => b.state !== 'cancelled');
  return {
    used: live.length,
    upcoming: live.filter(b => b.state === 'booked').length,
    months: new Set(live.map(b => String(b.at || '').slice(0, 7))).size,
    what: rulesFor(country).health.employerSees,
    /* Said on the screen, in the same words the employee is given. Two
       different sentences about the same confidentiality is how one of them
       turns out to be wrong. */
    confidential: rulesFor(country).health.confidential,
  };
}

/** The employee's own view: everything, because it is theirs. */
export function myView(bookings = [], staffId) {
  return bookings
    .filter(b => b.staffId === staffId)
    .sort((a, b) => String(b.at || '').localeCompare(String(a.at || '')));
}

/**
 * May this person book this service this way?
 *
 * The provider decides what it does down a wire and what needs somebody in
 * the room, and the service has its own answer too. Offering a video
 * physiotherapy appointment that the provider will then turn into a trip
 * across town is worse than not offering it.
 */
export function bookable(providerId, serviceId, way) {
  const p = provider(providerId);
  const s = employeeService(serviceId);
  if (!p) return { may: false, why: 'No provider is connected.' };
  if (!s) return { may: false, why: 'That is not something to book.' };
  if (!p.ways.includes(way)) {
    return { may: false, why: `${p.name} does not offer this ${way === 'video' ? 'by video' : 'that way'}.` };
  }
  if (!s.ways.includes(way)) {
    return { may: false, why: `${s.name} is not something that works ${way === 'video' ? 'down a wire' : 'that way'}.` };
  }
  return { may: true, why: null, mins: s.mins };
}

/** The ways both the provider and the service will actually do. */
export const waysFor = (providerId, serviceId) => {
  const p = provider(providerId);
  const s = employeeService(serviceId);
  if (!p || !s) return [];
  return p.ways.filter(w => s.ways.includes(w));
};

/** Is this provider's approval a checked fact, or only a claim? */
export function approval(providerId, country = HOME) {
  const p = provider(providerId);
  const h = rulesFor(country).health;
  if (!p) return null;
  return {
    body: p.approval.body,
    label: p.approval.label,
    number: p.approval.number,
    checked: !!p.approval.checked,
    say: p.approval.checked
      ? `${p.approval.label}, ${p.approval.body} ${p.approval.number}.`
      : `${p.name} is listed here as an occupational health provider. Their `
        + `approval has NOT been checked against ${h.approvedBy}'s register in `
        + `this build, and this screen will not claim it has.`,
    what: h.approvedWhat,
  };
}
